import { useState } from "react";
import { CheckSquare, Loader2, Square, Tags, X } from "lucide-react";
import { useAuth } from "../auth/useAuth";
import { authEnabled } from "../auth/config";
import { data } from "../lib/data";
import {
  type Stage,
  applyExperienceOverride,
  applyOverride,
  getStage,
  putOverride,
  useOverlayVersion,
} from "../lib/overlay";
import { getTags } from "../lib/queries";
import { TagEditor } from "../components/TagEditor";
import type { LensKey, Tag } from "../types";

// Admin-only triage desk: see how every experience and engagement sits across Draft / In review /
// Published / Archived, and move or re-tag several at once. Field-level prose edits stay on each
// item's own page; this screen is for bulk stage transitions and bulk tag changes. The content-write
// API merges (stage falls back to existing when omitted; fields spread over existing), so a bulk
// stage move never drops field edits and a bulk tag change never drops the stage.

const STAGES: Stage[] = ["draft", "in-review", "published", "archived"];
const STAGE_LABEL: Record<Stage, string> = {
  draft: "Draft",
  "in-review": "In review",
  published: "Published",
  archived: "Archived",
};

type Kind = "experience" | "engagement";
type TagKey = "industries" | "technologies" | "skills";

interface Item {
  id: string;
  kind: Kind;
  name: string;
  parent?: string;
  stage: Stage;
  summary: string;
  industries: string[];
  technologies: string[];
  skills: string[];
}

const TAG_DIMS: { key: TagKey; lens: LensKey; label: string; singular: string }[] = [
  { key: "industries", lens: "industry", label: "Industries", singular: "industry" },
  { key: "technologies", lens: "technology", label: "Technologies", singular: "technology" },
  { key: "skills", lens: "skill", label: "Skills", singular: "skill" },
];

function buildItems(): Item[] {
  const company = new Map(
    data.experiences.map((e) => [e.id, applyExperienceOverride(e).company]),
  );
  const exps: Item[] = data.experiences.map((e) => {
    const o = applyExperienceOverride(e);
    return {
      id: e.id,
      kind: "experience",
      name: o.company,
      summary: o.summary,
      stage: getStage(e.id, undefined),
      industries: o.industries,
      technologies: o.technologies,
      skills: o.skills,
    };
  });
  const engs: Item[] = data.projects.map((p) => {
    const o = applyOverride(p);
    return {
      id: p.id,
      kind: "engagement",
      name: o.title,
      summary: o.summary,
      parent: company.get(p.experienceId),
      stage: getStage(p.id, p.status),
      industries: o.industries,
      technologies: o.technologies,
      skills: o.skills,
    };
  });
  return [...engs, ...exps];
}

export function ContentStudio() {
  // authEnabled is a build-time constant, so branching before any hook is safe.
  if (!authEnabled) return <Notice body="Sign-in isn't configured in this build." />;
  return <Studio />;
}

function Notice({ body }: { body: string }) {
  return (
    <div className="wrap section">
      <p className="eyebrow">Admin · content readiness</p>
      <h1 className="display" style={{ fontSize: 34, marginTop: 8 }}>
        Content readiness
      </h1>
      <p className="section-sub">{body}</p>
    </div>
  );
}

function Studio() {
  const { isAdmin, getApiToken } = useAuth();
  useOverlayVersion(); // re-render when the overlay store changes (e.g. after a bulk write)

  const [stageFilter, setStageFilter] = useState<Stage | "all">("all");
  const [typeFilter, setTypeFilter] = useState<Kind | "all">("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tagPanel, setTagPanel] = useState(false);

  // Cheap to derive each render (~40 items); reflects the latest overlay store + filter state.
  const items = buildItems();
  const itemById = new Map(items.map((it) => [it.id, it]));
  const count = (type: Kind, stage: Stage | "all") =>
    items.filter((it) => it.kind === type && (stage === "all" || it.stage === stage)).length;
  const needle = q.trim().toLowerCase();
  const filtered = items.filter((it) => {
    if (stageFilter !== "all" && it.stage !== stageFilter) return false;
    if (typeFilter !== "all" && it.kind !== typeFilter) return false;
    if (needle && !`${it.name} ${it.parent ?? ""}`.toLowerCase().includes(needle)) return false;
    return true;
  });

  const allInViewSelected = filtered.length > 0 && filtered.every((it) => selected.has(it.id));
  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const toggleAllInView = () =>
    setSelected((s) => {
      const n = new Set(s);
      if (allInViewSelected) filtered.forEach((it) => n.delete(it.id));
      else filtered.forEach((it) => n.add(it.id));
      return n;
    });

  const runBulk = async (
    op: (id: string, token: string) => Promise<boolean>,
    done: (ok: number, fail: number) => string,
  ) => {
    const ids = [...selected];
    if (!ids.length) return;
    setErr(null);
    setMsg(null);
    setBusy(true);
    const token = await getApiToken();
    if (!token) {
      setErr("Couldn't get an access token — try signing in again.");
      setBusy(false);
      return;
    }
    let ok = 0;
    let fail = 0;
    for (const id of ids) {
      try {
        if (await op(id, token)) ok++;
        else fail++;
      } catch {
        fail++;
      }
    }
    setBusy(false);
    setSelected(new Set());
    setMsg(done(ok, fail));
  };

  const moveTo = (stage: Stage) =>
    runBulk(
      (id, token) => putOverride(id, { stage }, token),
      (ok, fail) => `Moved ${ok} to ${STAGE_LABEL[stage]}${fail ? ` · ${fail} failed` : ""}`,
    );

  const applyTags = (dim: (typeof TAG_DIMS)[number], tagIds: string[], mode: "add" | "remove") => {
    if (!tagIds.length) return;
    return runBulk(
      (id, token) => {
        const it = itemById.get(id);
        if (!it) return Promise.resolve(false);
        const cur = it[dim.key];
        const next =
          mode === "add"
            ? Array.from(new Set([...cur, ...tagIds]))
            : cur.filter((t) => !tagIds.includes(t));
        if (next.length === cur.length && next.every((t, i) => t === cur[i])) {
          return Promise.resolve(true); // no change for this item
        }
        return putOverride(id, { fields: { [dim.key]: JSON.stringify(next) } }, token);
      },
      (ok, fail) =>
        `${mode === "add" ? "Added" : "Removed"} ${dim.label.toLowerCase()} on ${ok}${fail ? ` · ${fail} failed` : ""}`,
    );
  };

  if (!isAdmin) return <Notice body="This page is for the site admin. Please sign in." />;

  return (
    <div className="wrap section content-studio">
      <p className="eyebrow">Admin · content readiness</p>
      <h1 className="display" style={{ fontSize: 36, marginTop: 8 }}>
        Content readiness
      </h1>
      <p className="section-sub" style={{ maxWidth: "70ch" }}>
        Move experiences and engagements through Draft, In review, Published, and Archived. Select
        rows to change stage or edit tags in bulk. Narrative text is still edited on each item's own
        page.
      </p>

      <div className="cs-readiness">
        {(["engagement", "experience"] as Kind[]).map((type) => (
          <div className="cs-metric-row" key={type}>
            <span className="cs-metric-label mono">
              {type === "engagement" ? "Engagements" : "Experiences"}
            </span>
            <div className="cs-stats">
              <button
                type="button"
                className={`cs-stat ${typeFilter === type && stageFilter === "all" ? "is-active" : ""}`}
                onClick={() => {
                  setTypeFilter(type);
                  setStageFilter("all");
                }}
              >
                <span className="cs-stat-n">{count(type, "all")}</span>
                <span className="cs-stat-label mono">All</span>
              </button>
              {STAGES.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-stage={s}
                  className={`cs-stat ${typeFilter === type && stageFilter === s ? "is-active" : ""}`}
                  onClick={() => {
                    setTypeFilter(type);
                    setStageFilter(s);
                  }}
                >
                  <span className="cs-stat-n">{count(type, s)}</span>
                  <span className="cs-stat-label mono">{STAGE_LABEL[s]}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="cs-controls">
        <div className="cs-filters">
          <input
            className="cs-search"
            placeholder="Search name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {(typeFilter !== "all" || stageFilter !== "all") && (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => {
                setTypeFilter("all");
                setStageFilter("all");
              }}
            >
              Show all
            </button>
          )}
        </div>
        <button type="button" className="btn btn-sm" onClick={toggleAllInView} disabled={!filtered.length}>
          {allInViewSelected ? <CheckSquare size={14} /> : <Square size={14} />} Select all in view
        </button>
      </div>

      {(msg || err) && <p className={err ? "eng-err" : "cs-msg"}>{err ?? msg}</p>}

      <ul className="cs-list">
        {filtered.map((it) => (
          <li key={`${it.kind}:${it.id}`} className={`cs-row ${selected.has(it.id) ? "is-sel" : ""}`}>
            <label className="cs-check">
              <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggle(it.id)} />
            </label>
            <a
              className="cs-name"
              href={it.kind === "experience" ? `/experience/${it.id}` : `/project/${it.id}`}
              target="_blank"
              rel="noopener"
              title={it.summary || undefined}
            >
              {it.parent && <span className="cs-parent">{it.parent} · </span>}
              {it.name}
            </a>
            <span className="cs-kind mono">{it.kind === "experience" ? "Experience" : "Engagement"}</span>
            <span className="cs-stage mono" data-stage={it.stage}>
              {STAGE_LABEL[it.stage]}
            </span>
          </li>
        ))}
        {!filtered.length && <li className="cs-empty">No items match.</li>}
      </ul>

      {selected.size > 0 && (
        <div className="cs-bulkbar">
          <span className="cs-bulk-count mono">
            {busy && <Loader2 size={14} className="cs-spin" />} {selected.size} selected
          </span>
          <span className="cs-bulk-group">
            <span className="cs-bulk-label mono">Move to</span>
            {STAGES.map((s) => (
              <button
                key={s}
                type="button"
                data-stage={s}
                className="btn btn-sm cs-stage-btn"
                disabled={busy}
                onClick={() => moveTo(s)}
              >
                {STAGE_LABEL[s]}
              </button>
            ))}
          </span>
          <button type="button" className="btn btn-sm" disabled={busy} onClick={() => setTagPanel((v) => !v)}>
            <Tags size={14} /> Tags
          </button>
          <button type="button" className="btn btn-sm" disabled={busy} onClick={() => setSelected(new Set())}>
            <X size={14} /> Clear
          </button>
        </div>
      )}

      {selected.size > 0 && tagPanel && (
        <div className="cs-tagpanel card card-pad">
          <p className="ds-card-kicker mono">Bulk edit tags on {selected.size} selected</p>
          {TAG_DIMS.map((dim) => (
            <TagBulkRow key={dim.key} dim={dim} disabled={busy} onApply={applyTags} />
          ))}
        </div>
      )}
    </div>
  );
}

function TagBulkRow({
  dim,
  disabled,
  onApply,
}: {
  dim: (typeof TAG_DIMS)[number];
  disabled: boolean;
  onApply: (dim: (typeof TAG_DIMS)[number], tagIds: string[], mode: "add" | "remove") => void;
}) {
  const [picked, setPicked] = useState<string[]>([]);
  const run = async (mode: "add" | "remove") => {
    await onApply(dim, picked, mode);
    setPicked([]);
  };
  return (
    <div className="cs-tagrow">
      <TagEditor
        label={dim.label}
        singular={dim.singular}
        value={picked}
        options={getTags(dim.lens) as Tag[]}
        onChange={setPicked}
      />
      <div className="cs-tagrow-actions">
        <button type="button" className="btn btn-sm" disabled={disabled || !picked.length} onClick={() => run("add")}>
          Add to selected
        </button>
        <button type="button" className="btn btn-sm" disabled={disabled || !picked.length} onClick={() => run("remove")}>
          Remove from selected
        </button>
      </div>
    </div>
  );
}
